import { useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Input, useToast } from '../../design-system';
import '../../org-admin/styles/Attendance.css';

// "I forgot to clock in this morning."
//
// Self-service on purpose: the firm's position is that people are trusted to
// record their own day, and a register that can only be fixed by chasing a
// manager is a register that quietly goes wrong. The safeguard is the trace —
// the server stamps every hand-entered session `manual` and it shows as such on
// the report and the manager's board.

// A local datetime for <input type="datetime-local">, which wants no timezone
// suffix and minute precision.
const localInput = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const todayKey = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function ManualHoursForm({ onSaved, defaultDateKey }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [dateKey, setDateKey] = useState(defaultDateKey || todayKey());
  // 9-to-6 is the common case, so it is pre-filled and usually just needs the
  // date changing.
  const [inAt, setInAt] = useState(() => localInput(new Date(new Date().setHours(9, 30, 0, 0))));
  const [outAt, setOutAt] = useState(() => localInput(new Date(new Date().setHours(18, 30, 0, 0))));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Changing the date moves both times with it — otherwise picking last Tuesday
  // and forgetting to change the times files the hours under today.
  const changeDate = (value) => {
    setDateKey(value);
    if (!value) return;
    const [y, m, d] = value.split('-').map(Number);
    for (const [current, set] of [[inAt, setInAt], [outAt, setOutAt]]) {
      const t = current.split('T')[1] || '09:30';
      set(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T${t}`);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiClient.post('/api/attendance/manual', {
        dateKey,
        // Sent as full ISO instants: the server decides which day they belong
        // to using the firm's timezone, not the browser's.
        inAt: new Date(inAt).toISOString(),
        outAt: new Date(outAt).toISOString(),
        note,
      });
      toast.success('Hours added');
      setOpen(false);
      setNote('');
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add those hours');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="att-manual-open" onClick={() => setOpen(true)}>
        Forgot to clock in? Add hours
      </button>
    );
  }

  return (
    <form className="att-manual" onSubmit={submit}>
      <p className="att-manual-intro">
        Add the hours you actually worked. They are recorded as entered by hand, so your
        manager can see the day was filled in rather than clocked.
      </p>

      {error && <div className="att-manual-error" role="alert">{error}</div>}

      <Input
        label="Date"
        id="manual-date"
        type="date"
        value={dateKey}
        max={todayKey()}
        onChange={(e) => changeDate(e.target.value)}
        disabled={saving}
      />
      <div className="att-manual-times">
        <Input
          label="From"
          id="manual-in"
          type="datetime-local"
          value={inAt}
          onChange={(e) => setInAt(e.target.value)}
          disabled={saving}
        />
        <Input
          label="To"
          id="manual-out"
          type="datetime-local"
          value={outAt}
          onChange={(e) => setOutAt(e.target.value)}
          disabled={saving}
        />
      </div>
      <Input
        label="Reason (optional)"
        id="manual-note"
        placeholder="e.g. Forgot to clock in"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={saving}
      />

      <div className="att-manual-actions">
        <Button type="submit" variant="primary" size="sm" loading={saving}>Add hours</Button>
        <Button type="button" variant="secondary" size="sm" disabled={saving} onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default ManualHoursForm;
